import { Component, ElementRef, HostListener, Input, ViewChild, computed, forwardRef, inject, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

import { animacaoSubir } from '../../../../chat/utils/chat-animacoes.utils';
import { MethodsUtils } from '../../../util/methods.utils';
import { PaisTelefone } from './models/pais-telefone.model';
import { TelefoneInternacional } from './models/telefone-internacional.model';
import { DDI_BRASIL, ISO_PAIS_PADRAO, MAX_DIGITOS_TELEFONE, MAX_DIGITOS_TELEFONE_BRASIL, PAISES_TELEFONE, URL_BANDEIRAS } from './utils/campo-telefone.constantes';

@Component({
  selector: 'app-campo-telefone',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './campo-telefone.component.html',
  styleUrl: './campo-telefone.component.css',
  animations: [animacaoSubir],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CampoTelefoneComponent), multi: true }
  ]
})
export class CampoTelefoneComponent implements ControlValueAccessor {
  @Input() label = '';
  @Input() placeholder = 'Digite o número';
  @Input() erro = '';
  @Input() disabled = false;

  @ViewChild('campoNumero') campoNumero?: ElementRef<HTMLInputElement>;
  @ViewChild('campoBuscaPais') campoBuscaPais?: ElementRef<HTMLInputElement>;

  readonly paises = PAISES_TELEFONE;

  aberto = signal(false);
  filtroPais = signal('');
  paisSelecionado = signal<PaisTelefone>(this.paisPorIso(ISO_PAIS_PADRAO));
  numero = signal('');

  readonly paisesFiltrados = computed(() => {
    const filtro = MethodsUtils.normalizarTexto(this.filtroPais().trim());
    if (!filtro) return this.paises;
    const digitos = filtro.replace(/\D/g, '');
    return this.paises.filter(pais =>
      MethodsUtils.normalizarTexto(pais.nome).includes(filtro)
      || (digitos.length > 0 && this.digitosDdi(pais).startsWith(digitos))
    );
  });

  readonly numeroExibicao = computed(() =>
    this.ehBrasil() ? MethodsUtils.formatarTelefoneNacional(this.numero()) : this.numero()
  );

  private readonly elementRef = inject(ElementRef);
  private onChange: (valor: TelefoneInternacional) => void = () => { /* */ };
  private onTouched: () => void = () => { /* */ };

  writeValue(valor: TelefoneInternacional | null): void {
    const pais = this.paises.find(p => this.digitosDdi(p) === valor?.ddi);
    if (pais) this.paisSelecionado.set(pais);
    this.numero.set((valor?.numero ?? '').replace(/\D/g, ''));
  }

  registerOnChange(fn: (valor: TelefoneInternacional) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(desabilitado: boolean): void {
    this.disabled = desabilitado;
  }

  alternar(): void {
    if (this.disabled) return;
    if (this.aberto()) {
      this.fechar();
      return;
    }
    this.filtroPais.set('');
    this.aberto.set(true);
    setTimeout(() => {
      this.campoBuscaPais?.nativeElement.focus();
      this.elementRef.nativeElement.querySelector('.campo-telefone-opcao-ativa')?.scrollIntoView({ block: 'center' });
    }, 30);
  }

  fechar(): void {
    this.aberto.set(false);
  }

  selecionarPais(pais: PaisTelefone): void {
    this.paisSelecionado.set(pais);
    this.numero.set(this.numero().slice(0, this.maxDigitos()));
    this.fechar();
    this.emitir();
    setTimeout(() => this.campoNumero?.nativeElement.focus(), 0);
  }

  aoDigitar(evento: Event): void {
    const input = evento.target as HTMLInputElement;
    this.numero.set(input.value.replace(/\D/g, '').slice(0, this.maxDigitos()));
    input.value = this.numeroExibicao();
    this.emitir();
  }

  aoTocar(): void {
    this.onTouched();
  }

  @HostListener('document:click', ['$event'])
  aoClicarFora(evento: MouseEvent): void {
    if (this.aberto() && !this.elementRef.nativeElement.contains(evento.target as Node)) this.fechar();
  }

  @HostListener('keydown.escape', ['$event'])
  aoPressionarEsc(evento: Event): void {
    if (!this.aberto()) return;
    evento.stopPropagation();
    this.fechar();
  }

  bandeira(pais: PaisTelefone): string {
    return `${URL_BANDEIRAS}/${pais.iso}.png`;
  }

  private ehBrasil(): boolean {
    return this.digitosDdi(this.paisSelecionado()) === DDI_BRASIL;
  }

  private maxDigitos(): number {
    return this.ehBrasil() ? MAX_DIGITOS_TELEFONE_BRASIL : MAX_DIGITOS_TELEFONE - this.digitosDdi(this.paisSelecionado()).length;
  }

  private digitosDdi(pais: PaisTelefone): string {
    return pais.codigo.replace(/\D/g, '');
  }

  private paisPorIso(iso: string): PaisTelefone {
    return PAISES_TELEFONE.find(p => p.iso === iso) ?? PAISES_TELEFONE[0];
  }

  private emitir(): void {
    this.onChange({ ddi: this.digitosDdi(this.paisSelecionado()), numero: this.numero() });
  }
}
